import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { motion } from 'framer-motion';
import { Calculator as CalcIcon, RotateCcw, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { PageWrapper } from '@/components/layout/PageWrapper';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { formatCurrency, formatPercent, BUNDESLAND_CONFIG } from '@/lib/utils';
import type { Bundesland } from '@/types';

interface CalculatorForm {
  purchasePrice: number;
  bundesland: Bundesland | '';
  additionalCostsPercent: number;
  renovationCosts: number;
  holdingMonths: number;
  holdingCostMonthly: number;
  rentCold: number;
  runningCostsMonthly: number;
  equityPercent: number;
  interestRate: number;
  repaymentRate: number;
}

const DEFAULT_VALUES: CalculatorForm = {
  purchasePrice: 250000,
  bundesland: 'bayern',
  additionalCostsPercent: 10,
  renovationCosts: 0,
  holdingMonths: 0,
  holdingCostMonthly: 0,
  rentCold: 850,
  runningCostsMonthly: 150,
  equityPercent: 20,
  interestRate: 3.5,
  repaymentRate: 2,
};

export default function CalculatorPage() {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { register, control, reset, setValue, watch } = useForm<CalculatorForm>({
    defaultValues: DEFAULT_VALUES,
  });

  const values = useWatch({ control });
  const bundesland = watch('bundesland') as Bundesland;

  // Auto-update Nebenkosten wenn Bundesland gewählt
  const autoAdditionalCosts = useMemo(() => {
    if (!bundesland || !values.purchasePrice) return null;
    const taxRate = BUNDESLAND_CONFIG[bundesland]?.transferTaxRate || 5;
    // Grunderwerbsteuer + Notar (1.5%) + Grundbuch (0.5%) + Makler (3.57%)
    return taxRate + 1.5 + 0.5 + 3.57;
  }, [bundesland, values.purchasePrice]);

  // Berechnungen
  const results = useMemo(() => {
    const purchasePrice = values.purchasePrice || 0;
    const additionalCostsPercent = values.additionalCostsPercent || 0;
    const additionalCosts = purchasePrice * (additionalCostsPercent / 100);
    const renovationCosts = values.renovationCosts || 0;
    const holdingCosts = (values.holdingMonths || 0) * (values.holdingCostMonthly || 0);

    // GESAMTINVESTITION
    const totalInvestment = purchasePrice + additionalCosts + renovationCosts + holdingCosts;

    // Finanzierung
    const equityPercent = values.equityPercent || 20;
    const equity = totalInvestment * (equityPercent / 100);
    const loanAmount = totalInvestment - equity;
    const interestRate = values.interestRate || 0;
    const repaymentRate = values.repaymentRate || 0;
    const annualPayment = loanAmount * ((interestRate + repaymentRate) / 100);
    const monthlyPayment = annualPayment / 12;

    // Mieteinnahmen
    const rentCold = values.rentCold || 0;
    const annualRent = rentCold * 12;

    // Laufende Kosten
    const runningCostsMonthly = values.runningCostsMonthly || 0;
    const annualRunningCosts = runningCostsMonthly * 12;

    // Zinsen (für EK-Rendite)
    const annualInterest = loanAmount * (interestRate / 100);

    // === RENDITEN ===

    // Bruttomietrendite = Jahresmiete / Kaufpreis
    const bruttoRendite = purchasePrice > 0 ? (annualRent / purchasePrice) * 100 : 0;

    // Nettomietrendite (klassisch) = Jahresmiete / (Kaufpreis + Nebenkosten)
    const nettoRenditeKlassisch = (purchasePrice + additionalCosts) > 0
      ? (annualRent / (purchasePrice + additionalCosts)) * 100
      : 0;

    // Nettomietrendite (real) = Jahresmiete / GESAMTINVESTITION
    const nettoRenditeReal = totalInvestment > 0
      ? (annualRent / totalInvestment) * 100
      : 0;

    // Eigenkapitalrendite = (Miete - Zinsen - laufende Kosten) / Eigenkapital
    const eigenkapitalRendite = equity > 0
      ? ((annualRent - annualInterest - annualRunningCosts) / equity) * 100
      : 0;

    // === CASHFLOW ===
    const cashflowBrutto = rentCold;
    const cashflowNachKosten = rentCold - runningCostsMonthly;
    const cashflowNetto = rentCold - runningCostsMonthly - monthlyPayment;

    // Jahresüberschuss
    const jahresueberschuss = cashflowNetto * 12;

    // EK-Rückfluss-Dauer
    const ekRueckflussDauer = cashflowNetto > 0 ? equity / (cashflowNetto * 12) : null;

    return {
      // Kosten
      additionalCosts,
      renovationCosts,
      holdingCosts,
      totalInvestment,

      // Finanzierung
      equity,
      loanAmount,
      monthlyPayment,

      // Renditen
      bruttoRendite,
      nettoRenditeKlassisch,
      nettoRenditeReal,
      eigenkapitalRendite,

      // Cashflow
      cashflowBrutto,
      cashflowNachKosten,
      cashflowNetto,
      jahresueberschuss,
      ekRueckflussDauer,
    };
  }, [values]);

  const handleReset = () => {
    reset(DEFAULT_VALUES);
  };

  const handleAutoCalcCosts = () => {
    if (autoAdditionalCosts) {
      setValue('additionalCostsPercent', Math.round(autoAdditionalCosts * 10) / 10);
    }
  };

  return (
    <PageWrapper>
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-background-subtle flex items-center justify-center">
              <CalcIcon className="w-5 h-5 text-foreground-muted" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">Rendite-Rechner</h1>
              <p className="text-sm text-foreground-muted">Schnell eine Immobilie durchrechnen</p>
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={handleReset}>
            <RotateCcw className="w-4 h-4 mr-2" />
            Zurücksetzen
          </Button>
        </div>

        {/* Ergebnis-Header */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div>
            <p className="metric-label">Bruttomietrendite</p>
            <p className="text-metric">{formatPercent(results.bruttoRendite)}</p>
          </div>
          <div>
            <p className="metric-label">Nettomietrendite</p>
            <p className="text-metric">{formatPercent(results.nettoRenditeReal)}</p>
            {results.renovationCosts > 0 || results.holdingCosts > 0 ? (
              <p className="text-xs text-foreground-muted">inkl. Sanierung + Haltekosten</p>
            ) : null}
          </div>
          <div>
            <p className="metric-label">EK-Rendite</p>
            <p className={`text-metric ${results.eigenkapitalRendite >= 0 ? '' : 'text-negative'}`}>
              {formatPercent(results.eigenkapitalRendite)}
            </p>
          </div>
          <div>
            <p className="metric-label">Cashflow / Monat</p>
            <p className={`text-metric ${results.cashflowNetto >= 0 ? 'text-positive' : 'text-negative'}`}>
              {results.cashflowNetto >= 0 ? '+' : ''}{formatCurrency(results.cashflowNetto * 100)}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Linke Spalte: Eingaben */}
          <div className="space-y-6">
            {/* Objekt */}
            <Card className="p-5">
              <h3 className="font-medium mb-4">Objekt</h3>
              <div className="space-y-4">
                <div>
                  <Label>Kaufpreis (€)</Label>
                  <Input
                    type="number"
                    step="1000"
                    {...register('purchasePrice', { valueAsNumber: true })}
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label>Kaufnebenkosten</Label>
                    {autoAdditionalCosts && (
                      <button
                        type="button"
                        onClick={handleAutoCalcCosts}
                        className="text-xs text-info hover:underline"
                      >
                        Auto: ~{autoAdditionalCosts.toFixed(1)}%
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      {...register('additionalCostsPercent', { valueAsNumber: true })}
                      className="flex-1"
                    />
                    <span className="text-foreground-muted">%</span>
                    <span className="text-sm text-foreground-muted w-24 text-right">
                      {formatCurrency(results.additionalCosts * 100)}
                    </span>
                  </div>
                </div>
                <div>
                  <Label>Bundesland (für Grunderwerbsteuer)</Label>
                  <select className="input w-full" {...register('bundesland')}>
                    <option value="">Bitte wählen...</option>
                    {Object.entries(BUNDESLAND_CONFIG).map(([key, config]) => (
                      <option key={key} value={key}>
                        {config.label} ({config.transferTaxRate}%)
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Card>

            {/* Mieteinnahmen */}
            <Card className="p-5">
              <h3 className="font-medium mb-4">Mieteinnahmen</h3>
              <div className="space-y-4">
                <div>
                  <Label>Kaltmiete / Monat (€)</Label>
                  <Input
                    type="number"
                    step="10"
                    {...register('rentCold', { valueAsNumber: true })}
                  />
                  <p className="text-xs text-foreground-muted mt-1">
                    Jahresmiete: {formatCurrency((values.rentCold || 0) * 12 * 100)}
                  </p>
                </div>
                <div>
                  <Label>Laufende Kosten / Monat (€)</Label>
                  <Input
                    type="number"
                    step="10"
                    {...register('runningCostsMonthly', { valueAsNumber: true })}
                  />
                  <p className="text-xs text-foreground-muted mt-1">
                    Nicht umlagefähig (Hausgeld, Rücklage, Verwaltung)
                  </p>
                </div>
              </div>
            </Card>

            {/* Finanzierung */}
            <Card className="p-5">
              <h3 className="font-medium mb-4">Finanzierung</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between mb-1">
                    <Label>Eigenkapital</Label>
                    <span className="text-sm">{formatCurrency(results.equity * 100)}</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      step="5"
                      className="flex-1"
                      {...register('equityPercent', { valueAsNumber: true })}
                    />
                    <span className="w-12 text-right">{values.equityPercent}%</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Zinssatz (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      {...register('interestRate', { valueAsNumber: true })}
                    />
                  </div>
                  <div>
                    <Label>Tilgung (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      {...register('repaymentRate', { valueAsNumber: true })}
                    />
                  </div>
                </div>
              </div>
            </Card>

            {/* Erweiterte Optionen */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm text-foreground-muted hover:text-foreground"
              >
                {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Erweiterte Optionen (Sanierung, Haltekosten)
              </button>

              {showAdvanced && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-4 space-y-4"
                >
                  <Card className="p-5">
                    <h3 className="font-medium mb-4">Sanierungskosten</h3>
                    <div>
                      <Label>Sanierung gesamt (€)</Label>
                      <Input
                        type="number"
                        step="1000"
                        {...register('renovationCosts', { valueAsNumber: true })}
                      />
                    </div>
                  </Card>

                  <Card className="p-5">
                    <h3 className="font-medium mb-4">Haltekosten</h3>
                    <p className="text-xs text-foreground-muted mb-3">
                      Kosten während Sanierung/Leerstand bevor Mieteinnahmen fließen
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Dauer (Monate)</Label>
                        <Input
                          type="number"
                          step="1"
                          {...register('holdingMonths', { valueAsNumber: true })}
                        />
                      </div>
                      <div>
                        <Label>Kosten / Monat (€)</Label>
                        <Input
                          type="number"
                          step="100"
                          {...register('holdingCostMonthly', { valueAsNumber: true })}
                        />
                      </div>
                    </div>
                    {results.holdingCosts > 0 && (
                      <p className="text-sm mt-2">
                        Haltekosten gesamt: <span className="font-medium text-negative">{formatCurrency(results.holdingCosts * 100)}</span>
                      </p>
                    )}
                  </Card>
                </motion.div>
              )}
            </div>
          </div>

          {/* Rechte Spalte: Zusammenfassung */}
          <div className="space-y-6">
            {/* Kostenübersicht */}
            <Card className="p-5">
              <h3 className="font-medium mb-4">Kostenübersicht</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Kaufpreis</span>
                  <span>{formatCurrency((values.purchasePrice || 0) * 100)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground-muted">+ Nebenkosten</span>
                  <span>{formatCurrency(results.additionalCosts * 100)}</span>
                </div>
                {results.renovationCosts > 0 && (
                  <div className="flex justify-between">
                    <span className="text-foreground-muted">+ Sanierung</span>
                    <span>{formatCurrency(results.renovationCosts * 100)}</span>
                  </div>
                )}
                {results.holdingCosts > 0 && (
                  <div className="flex justify-between">
                    <span className="text-foreground-muted">+ Haltekosten</span>
                    <span className="text-negative">{formatCurrency(results.holdingCosts * 100)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-3 border-t border-border font-medium">
                  <span>Gesamtkosten</span>
                  <span>{formatCurrency(results.totalInvestment * 100)}</span>
                </div>
              </div>
            </Card>

            {/* Finanzierungsstruktur */}
            <Card className="p-5">
              <h3 className="font-medium mb-4">Finanzierungsstruktur</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Eigenkapital</span>
                  <span>{formatCurrency(results.equity * 100)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Darlehen</span>
                  <span>{formatCurrency(results.loanAmount * 100)}</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-border">
                  <span className="text-foreground-muted">Rate / Monat</span>
                  <span className="font-medium">{formatCurrency(results.monthlyPayment * 100)}</span>
                </div>
              </div>
            </Card>

            {/* Cashflow */}
            <Card className="p-5">
              <h3 className="font-medium mb-4">Cashflow (monatlich)</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-foreground-muted">Miete / Monat</span>
                  <span className="text-positive">+{formatCurrency((values.rentCold || 0) * 100)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground-muted">- Laufende Kosten</span>
                  <span className="text-negative">-{formatCurrency((values.runningCostsMonthly || 0) * 100)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-foreground-muted">- Finanzierung</span>
                  <span className="text-negative">-{formatCurrency(results.monthlyPayment * 100)}</span>
                </div>
                <div className="flex justify-between pt-3 border-t border-border">
                  <span className="font-medium">Cashflow / Monat</span>
                  <span className={`font-semibold ${results.cashflowNetto >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {results.cashflowNetto >= 0 ? '+' : ''}{formatCurrency(results.cashflowNetto * 100)}
                  </span>
                </div>
              </div>
            </Card>

            {/* Kennzahlen */}
            <Card className="p-5">
              <h3 className="font-medium mb-4">Kennzahlen</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted flex items-center gap-1">
                    Bruttomietrendite
                    <span className="text-xs">(Miete/Kaufpreis)</span>
                  </span>
                  <span>{formatPercent(results.bruttoRendite)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted flex items-center gap-1">
                    Nettomietrendite
                    <span className="text-xs">(klassisch)</span>
                  </span>
                  <span>{formatPercent(results.nettoRenditeKlassisch)}</span>
                </div>
                {(results.renovationCosts > 0 || results.holdingCosts > 0) && (
                  <div className="flex justify-between items-center">
                    <span className="text-foreground-muted flex items-center gap-1">
                      Nettomietrendite
                      <span className="text-xs text-deal-maybe">(real)</span>
                    </span>
                    <span className="text-deal-maybe">{formatPercent(results.nettoRenditeReal)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-foreground-muted">Eigenkapitalrendite</span>
                  <span className={results.eigenkapitalRendite >= 0 ? '' : 'text-negative'}>
                    {formatPercent(results.eigenkapitalRendite)}
                  </span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-border">
                  <span className="text-foreground-muted">Jahresüberschuss</span>
                  <span className={`font-medium ${results.jahresueberschuss >= 0 ? 'text-positive' : 'text-negative'}`}>
                    {results.jahresueberschuss >= 0 ? '+' : ''}{formatCurrency(results.jahresueberschuss * 100)}
                  </span>
                </div>
                {results.ekRueckflussDauer && results.ekRueckflussDauer > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-foreground-muted">EK-Rückfluss-Dauer</span>
                    <span>{results.ekRueckflussDauer.toFixed(1)} Jahre</span>
                  </div>
                )}
              </div>
            </Card>

            {/* Hinweis */}
            <div className="flex items-start gap-2 p-3 bg-background-subtle rounded-lg">
              <Info className="w-4 h-4 text-foreground-muted mt-0.5 flex-shrink-0" />
              <p className="text-xs text-foreground-muted">
                Die "reale" Nettomietrendite berücksichtigt Sanierungskosten und Haltekosten als Teil der Gesamtinvestition.
                Das gibt ein ehrlicheres Bild als die klassische Berechnung.
              </p>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
}